<template>
  <b-container
    fluid
    class="paged-grid"
  >
    <b-row align-h="center">
      <b-col
        class="px-0"
        cols="12"
      >
        <h1 class="font-weight-bold">
          {{ $t(mainTableTitle) }}
        </h1>
      </b-col>
      <b-col
        v-if="showAggregator"
        cols="12"
        class="text-center paged-grid__list-aggregator"
      >
        <list-aggregator
          :modal-table-items="listSetter"
          :modal-title="$t(modalTitle)"
          :modal-item-name-key="modalItemNameKey"
          :modal-item-id-key="modalItemIdKey"
          :configuration-mode="configurationMode"
          :modal-table-header="$t(modalTableHeader)"
          @update-selection="updateSelection"
        />
      </b-col>
    </b-row>
    <b-row>
      <s-table
        :busy="loading"
        :items="mainTableItems"
        :fields="mainTableFields"
        class="paged-grid__table no-align"
      >
        <template #head(check)>
          <div class="paged-grid__check-wrapper">
            <b-form-checkbox
              @change="selectAll"
              :checked="selectAllHandler"
            />
          </div>
        </template>
        <template #cell(check)="data">
          <b-form-checkbox
            v-model="data.item.selected"
            @change="singleSelectHandler($event, data.item, data.index)"
          />
        </template>
      </s-table>
    </b-row>
    <b-row>
      <grid-paginator
        v-model="pageHandler"
        :per-page="pagination.perPage"
        :total-rows="pagination.totalRows"
        class="alarms-classification__paginator pt-3"
        v-bind="paginatorOptions"
      />
    </b-row>
  </b-container>
</template>
<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .paged-grid
    &__list-aggregator
      min-height: 28px
    &__table-container
      display: flex
      flex-direction: column
      overflow-y: hidden
    &__table ::v-deep
      overflow-y: hidden
      display: flex
      max-height: 53vh
      table
        display: flex
        flex-direction: column
        min-width: 450px
        overflow: auto
        th:first-child, td:first-child
          width: 40px
      thead
        position: sticky
        top: 0
        z-index: 2
      tbody, thead
        tr
          display: table
          width: 100%
          table-layout: fixed
    &__loader
      font-size: 5em
      min-height: 400px
      margin-bottom: 1em
    &__check-wrapper
      width: 40px !important
      position: relative
      & .custom-control
        position: absolute
        bottom: 0
    ::v-deep &__cell
      &-check
        width: 40px
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
<script>
import cloneDeep from 'lodash/cloneDeep';
import ListAggregator from '@/components/listAggregator';
import GridPaginator from '@/components/lists/Paginator';

export default {
  name: 'PagedGrid',
  props: {
    showAggregator: {
      type: Boolean,
      default: true,
    },
    configurationMode: {
      type: Boolean,
      default: false,
    },
    modalItemNameKey: {
      type: String,
      default: 'installationName',
    },
    modalItemIdKey: {
      type: String,
      default: 'installationId',
    },
    modalTableHeader: {
      type: String,
      default: 'entities.installations',
    },
    modalTitle: {
      type: String,
      default: 'page-name.my-sigma-alarms-configuration',
    },
    mainTableTitle: {
      type: String,
      default: ('my-sigma.select-installations-tab.installations-table-title'),
    },
    mainTableFields: {
      type: [Object, Array],
      required: true,
    },
    mainTableItems: {
      type: Array,
      required: true,
    },
    selectedItems: {
      type: Array,
    },
    backup: {
      type: Array,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    pagination: {
      type: Object,
    },
    /**
     * Proxy para customizar propriedades do componente de paginação.
     * É passado por v-bind para o paginador.
     */
    paginatorOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  components: {
    ListAggregator,
    GridPaginator,
  },
  data: () => ({
    previousSelected: [],
  }),
  computed: {
    previousSelectedIds() {
      return this.mapById(this.previousSelected);
    },
    listSetter() {
      return (this.backup || []).length
        ? this.backup
        : this.previousSelected;
    },
    pageHandler: {
      get() {
        return this.pagination.currentPage;
      },
      set(value) {
        this.$emit('page-changed', value);
      },
    },
    selectAllHandler() {
      return this.currentPageSelectedItens.length === this.mainTableItems.length;
    },
    currentPageSelectedItens() {
      return this.mainTableItems.filter(({ selected }) => selected);
    },
  },
  methods: {
    mapById(list) {
      return (list || []).map(item => item[this.modalItemIdKey]);
    },
    selectAll(checked) {
      this.mainTableItems.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.selected = checked;
        return checked ? this.addItem(item) : this.removeItem(item);
      });
      this.$emit('input', this.previousSelected);
    },
    updateSelection(newItemsSelected) {
      const selectedIds = this.mapById(newItemsSelected);
      this.previousSelected = newItemsSelected;
      this.mainTableItems = this.locationListSelect(selectedIds);
      this.$emit('input', this.previousSelected);
    },
    locationListSelect(selectedIds = this.previousSelectedIds) {
      const list = this.mainTableItems;
      list.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.selected = selectedIds.includes(item[this.modalItemIdKey]);
      });
      return list;
    },
    singleSelectHandler(selected, payload, index) {
      const handler = selected
        ? this.addItem(payload, index)
        : this.removeItem(payload, index);
      this.$emit('input', this.previousSelected);
      return handler;
    },
    removeItem(payload) {
      this.filterPreviousSelection(payload[this.modalItemIdKey]);
    },
    addItem(payload) {
      if (this.previousSelectedIds.includes(payload[this.modalItemIdKey])) return;
      this.previousSelected.push(payload);
    },
    filterPreviousSelection(diffId) {
      this.previousSelected = this.previousSelected
        .filter(item => item[this.modalItemIdKey] !== diffId);
    },
    handleBackup() {
      this.previousSelected = cloneDeep(this.backup);
    },
  },
  beforeUpdate() {
    this.handleBackup();
  },
  created() {
    this.handleBackup();
  },
};
</script>
